import {
  useConnectExternalServiceToAccountMutation,
  useExternalServiceUserInfoSuspenseQuery,
  useRejectConnectExternalServiceToAccountMutation
} from '@anytag/graphql';
import { anytagLogo } from '@anytag/shared-assets';
import { Button } from '@anytag/shared-atoms';
import { useQueryHelper } from '@anytag/shared-hooks';
import { InfluencerAvatar, InitialLoading } from '@anytag/shared-molecules';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessSearchSchemaType } from './schemaTypes';

export const AccessPage = () => {
  const { t } = useTranslation();
  const { code } = useSearch({ from: '/_private-routes/access/' }) as AccessSearchSchemaType;

  if (!code) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black-10">
        <div className="flex flex-col items-center rounded-lg bg-white p-8 shadow-md">
          <div className="mb-4 text-lg font-semibold text-primary">{t('SlackAuthorizePage.NoCode')}</div>
          <span>{t('SlackAuthorizePage.TryAgain')}</span>
          <span>{t('SlackAuthorizePage.YouCanLeave')}</span>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<InitialLoading />}>
      <SlackAuthorizePage code={code} />
    </Suspense>
  );
};

const SlackAuthorizePage = ({ code }: { code: string }) => {
  const { t, enqueueSnackbar } = useQueryHelper();
  const navigate = useNavigate();
  const { data, error } = useExternalServiceUserInfoSuspenseQuery({ variables: { code } });
  const { callConnectExternalServiceToAccount, loading: isLoadingConnectExternalServiceToAccount } =
    useConnectExternalServiceToAccountMutation({
      variables: { code },
      onCompleted: () => {
        enqueueSnackbar(t('SlackAuthorizePage.AccessGranted'), { variant: 'success' });
        navigate({ to: '/access/authorized' });
      }
    });
  const { callRejectConnectExternalServiceToAccount, loading: isLoadingRejectConnectExternalServiceToAccount } =
    useRejectConnectExternalServiceToAccountMutation({
      variables: { code },
      onCompleted: () => {
        enqueueSnackbar(t('SlackAuthorizePage.AccessRejected'), { variant: 'info' });
        navigate({ to: '/access/declined' });
      }
    });

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black-10">
        <div className="flex flex-col items-center space-y-2 rounded-lg bg-white p-8 shadow-md">
          <div className="mb-2 text-lg font-semibold text-primary">{t('SlackAuthorizePage.FailedToLoadUserInfo')}</div>
          <i className="text-sm text-primary">{error?.message}</i>
          <span>{t('SlackAuthorizePage.TryAgain')}</span>
          <span>{t('SlackAuthorizePage.YouCanLeave')}</span>
        </div>
      </div>
    );
  }
  if (!data?.externalServiceUserInfo) return <InitialLoading />;

  const { displayName, email, avatar, realName, username } = {
    ...data?.externalServiceUserInfo
  };

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-md">
        <div className="mb-6 flex flex-col items-center">
          <div className="flex items-center gap-6">
            <img src={anytagLogo} alt="AnyTag" className="h-12 w-12" />
            <div className="flex flex-col items-center">
              <span className="text-3xl leading-3 text-secondary">&rarr;</span>
              <span className="text-3xl leading-3 text-secondary">&larr;</span>
            </div>
            <InfluencerAvatar src={avatar} size="64px" className="mb-2" />
          </div>
          <div className="text-lg font-semibold">{displayName || realName || username || '-'}</div>
          {email ? <div className="text-sm text-primary">{email}</div> : null}
        </div>
        <div className="mb-6 text-center text-base font-medium text-primary">
          <b>AnyTag</b> {t('SlackAuthorizePage.WillGrantAccess')}
        </div>
        <ul className="mb-6 list-disc space-y-1 pl-6 text-center text-sm text-primary">
          <li>{t('SlackAuthorizePage.OnceAuthorized')}</li>
        </ul>
        <div className="flex gap-4">
          <Button
            loading={{
              showIcon: true,
              status: isLoadingRejectConnectExternalServiceToAccount
            }}
            color="danger"
            className="flex-1"
            tooltip={{
              help: t('SlackAuthorizePage.DeclineHelp')
            }}
            title={t('Button.Decline')}
            onClick={() => callRejectConnectExternalServiceToAccount()}
          />
          <Button
            loading={{
              showIcon: true,
              status: isLoadingConnectExternalServiceToAccount
            }}
            color="primary"
            variant="solid"
            className="flex-1"
            title={t('Button.Authorize')}
            onClick={() => callConnectExternalServiceToAccount()}
          />
        </div>
      </div>
    </div>
  );
};
